import React, { useCallback, useEffect, useState } from 'react';
import {
  X, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle,
  Loader2, RefreshCw, ChevronRight, MinusCircle,
} from 'lucide-react';

import regionService from '../../services/RegionService';
import {
  REGION_SHEETS,
  downloadRegionTemplate, readRegionWorkbook, validateRegionWorkbook,
  readProvinceRows, readDistrictRows, readElectorateRows, readStationRows,
  findProvinceCode, findDistrictCode, findElectorateCode,
} from '../../utils/regionImport';

const BTN_PRIMARY = 'flex items-center gap-2 h-9 px-5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST = 'flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';


async function loadExisting() {
  const settle = (fn, reader) => fn().then(reader).catch(() => []);
  const [provinces, districts, electorates, stations] = await Promise.all([
    settle(() => regionService.GetProvinceList(), readProvinceRows),
    settle(() => regionService.GetDistrictList(), readDistrictRows),
    settle(() => regionService.GetElectorateList(), readElectorateRows),
    settle(() => regionService.GetPoliceStationList(), readStationRows),
  ]);
  return { provinces, districts, electorates, stations };
}

const STATUS_PILL = {
  new:    { cls: 'text-emerald-700', Icon: CheckCircle2, text: 'Will be added' },
  exists: { cls: 'text-gray-400',    Icon: MinusCircle,  text: 'Already exists — skipped' },
  error:  { cls: 'text-red-700',     Icon: AlertCircle,  text: '' },
};

export default function ImportRegionModal({ isOpen, onClose, onImported }) {
  const [existing, setExisting] = useState(null);
  const [loadingLists, setLoadingLists] = useState(false);

  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);      // raw rows, kept for re-validation
  const [checked, setChecked] = useState(null);    // validateRegionWorkbook output
  const [parseError, setParseError] = useState('');

  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ label: '', done: 0, total: 0 });
  const [results, setResults] = useState(null);

  const refreshLists = useCallback(async () => {
    setLoadingLists(true);
    const lists = await loadExisting();
    setExisting(lists);
    setLoadingLists(false);
    return lists;
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setFileName(''); setParsed(null); setChecked(null);
    setParseError(''); setResults(null);
    setProgress({ label: '', done: 0, total: 0 });
    refreshLists();
  }, [isOpen, refreshLists]);

  // Re-check the file whenever the lists are reloaded, so "already exists"
  // reflects what is on the server right now.
  useEffect(() => {
    if (parsed && existing) setChecked(validateRegionWorkbook(parsed, existing));
  }, [parsed, existing]);

  const handleFile = async (file) => {
    if (!file) return;
    setParseError(''); setResults(null); setChecked(null); setParsed(null);
    setFileName(file.name);
    try {
      const rows = await readRegionWorkbook(file);
      setParsed(rows);
      const lists = existing || (await refreshLists());
      setChecked(validateRegionWorkbook(rows, lists));
    } catch (err) {
      setParseError(err?.message || 'The file could not be read.');
      setFileName('');
    }
  };

  /* ── The import itself ─────────────────────────────────────────────────
   * One level at a time, and after each level its list is read back so the
   * next level can resolve its parent to the code the server just allocated.
   * Nothing is written unless the whole workbook validated, which is why a
   * bad electorate row can't leave half the provinces created.
   */
  const runImport = async () => {
    setImporting(true);
    setResults(null);

    const failed = [];
    const added = { provinces: 0, districts: 0, electorates: 0, stations: 0 };

    const newOf = (key) => checked[key].filter((r) => r.status === 'new');
    const totalRows = REGION_SHEETS.reduce((n, d) => n + newOf(d.key).length, 0);
    let done = 0;

    const step = (label) => setProgress({ label, done, total: totalRows });
    const tick = (label) => { done += 1; setProgress({ label, done, total: totalRows }); };

    const note = (sheet, row, err) => failed.push({
      sheet, _row: row._row, name: row.name,
      message: err?.response?.data?.Message || err?.message || 'Failed',
    });

    let lists = existing;

    try {
      /* 1 — Provinces */
      const provRows = newOf('provinces');
      if (provRows.length) {
        step('Provinces');
        for (const row of provRows) {
          try {
            await regionService.AddProvince({ p_provinceName: row.name });
            added.provinces += 1;
          } catch (err) { note('Provinces', row, err); }
          tick('Provinces');
        }
        lists = { ...lists, provinces: await regionService.GetProvinceList().then(readProvinceRows).catch(() => lists.provinces) };
      }

      /* 2 — Districts */
      const distRows = newOf('districts');
      if (distRows.length) {
        step('Districts');
        for (const row of distRows) {
          const provinceCode = findProvinceCode(lists.provinces, row.province);
          if (!provinceCode) {
            note('Districts', row, new Error(`Province "${row.province}" was not created, so this district was skipped.`));
          } else {
            try {
              await regionService.AddDistrict({
                p_districtProvinceCode: provinceCode,
                p_districtName: row.name,
              });
              added.districts += 1;
            } catch (err) { note('Districts', row, err); }
          }
          tick('Districts');
        }
        lists = { ...lists, districts: await regionService.GetDistrictList().then(readDistrictRows).catch(() => lists.districts) };
      }

      /* 3 — Electorates */
      const elecRows = newOf('electorates');
      if (elecRows.length) {
        step('Electorates');
        for (const row of elecRows) {
          const provinceCode = findProvinceCode(lists.provinces, row.province);
          const districtCode = findDistrictCode(lists.districts, provinceCode, row.district);
          if (!provinceCode || !districtCode) {
            note('Electorates', row, new Error(`Could not resolve ${row.district}, ${row.province}.`));
          } else {
            try {
              await regionService.AddElectorate({
                p_electorateArea: row.name,
                p_electorateProvince: provinceCode,
                p_electorateDistrict: districtCode,
              });
              added.electorates += 1;
            } catch (err) { note('Electorates', row, err); }
          }
          tick('Electorates');
        }
        lists = { ...lists, electorates: await regionService.GetElectorateList().then(readElectorateRows).catch(() => lists.electorates) };
      }

      /* 4 — Police Stations */
      const stnRows = newOf('stations');
      if (stnRows.length) {
        step('Police Stations');
        for (const row of stnRows) {
          const provinceCode = findProvinceCode(lists.provinces, row.province);
          const districtCode = findDistrictCode(lists.districts, provinceCode, row.district);
          const electorateCode = findElectorateCode(lists.electorates, provinceCode, districtCode, row.electorate);
          if (!provinceCode || !districtCode) {
            note('Police Stations', row, new Error(`Could not resolve ${row.district}, ${row.province}.`));
          } else if (!electorateCode) {
            note('Police Stations', row, new Error(`Electorate "${row.electorate}" was not created, so this station was skipped.`));
          } else {
            try {
              // A station stores its own province and district codes; the
              // procedure rejects the insert if any of the four is missing.
              await regionService.AddPoliceStation({
                p_stationName: row.name,
                p_stationProvinceCode: provinceCode,
                p_stationDistrictCode: districtCode,
                p_stationElectorateCode: electorateCode,
                p_status: 1,
              });
              added.stations += 1;
            } catch (err) { note('Police Stations', row, err); }
          }
          tick('Police Stations');
        }
      }
    } finally {
      setImporting(false);
    }

    setResults({ added, failed });
    setExisting(await loadExisting());     // so a second upload sees the new rows

    const total = added.provinces + added.districts + added.electorates + added.stations;
    if (total && onImported) onImported(total);
  };

  if (!isOpen) return null;

  const blocked = checked ? checked.totalBad > 0 : false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={importing ? undefined : onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-primary-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Import Region from Excel</h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                One workbook — Provinces, Districts, Electorates, Police Stations — imported top to bottom.
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={importing}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Step 1 — template */}
          <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-gray-800">1. Download the template</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Four sheets, filled from the master lists already on the server — so every
                parent name is spelled exactly as the system stores it. Add your new rows
                underneath; the existing ones are recognised and skipped. No codes are typed.
              </p>
            </div>
            <button onClick={() => downloadRegionTemplate(existing || {})}
              disabled={loadingLists} className={`${BTN_GHOST} shrink-0`}>
              {loadingLists ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              {loadingLists ? 'Loading lists…' : 'Download Template'}
            </button>
          </div>

          {/* Step 2 — upload */}
          <div className="p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-gray-800">2. Upload the filled file</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {fileName
                    ? `Selected: ${fileName}`
                    : '.xlsx or .xlsm — keep the four sheet names and the header rows as they are.'}
                </p>
              </div>
              <label className={`${BTN_GHOST} shrink-0 cursor-pointer`}>
                <Upload size={13} /> Choose File
                <input type="file" accept=".xlsx,.xlsm,.xls" className="hidden" disabled={importing}
                  onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
            </div>

            {parseError && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-[12px] text-red-700">{parseError}</p>
              </div>
            )}
          </div>

          {/* Step 3 — what was read */}
          {checked && !results && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900">3. Check the rows</h3>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                    <CheckCircle2 size={11} /> {checked.totalNew} to add
                  </span>
                  {checked.totalBad > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold">
                      <AlertCircle size={11} /> {checked.totalBad} with problems
                    </span>
                  )}
                </div>
              </div>

              {/* Per-sheet tally */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border-b border-gray-200">
                {checked.summary.map((s) => (
                  <div key={s.key} className="bg-white px-4 py-3">
                    <p className="text-[11px] font-semibold text-gray-500">{s.sheet}</p>
                    <p className="text-[15px] font-bold text-gray-900 tabular-nums mt-0.5">{s.add}</p>
                    <p className="text-[10px] text-gray-400">
                      to add{s.skip ? ` · ${s.skip} existing` : ''}{s.bad ? ` · ${s.bad} bad` : ''}
                    </p>
                  </div>
                ))}
              </div>

              <div className="overflow-auto max-h-[38vh]">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                      {['Sheet', 'Row', 'Name', 'Parent', 'Status'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-[12px] font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {REGION_SHEETS.flatMap((def) => checked[def.key].map((r) => {
                      const pill = STATUS_PILL[r.status];
                      const parent = [r.province, r.district, r.electorate].filter(Boolean).join(' › ');
                      return (
                        <tr key={`${def.key}-${r._row}`}
                          className={`border-b border-gray-100 last:border-0 ${r.status === 'error' ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{def.sheet}</td>
                          <td className="px-4 py-3 text-gray-400 tabular-nums">{r._row}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                            {r.name || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {parent || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {r.status === 'error' ? (
                              <ul className="space-y-0.5">
                                {r.errors.map((e, i) => (
                                  <li key={i} className="text-[11px] text-red-700 flex items-start gap-1">
                                    <span className="text-red-400">•</span>{e}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${pill.cls}`}>
                                <pill.Icon size={12} /> {pill.text}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
              </div>

              {blocked && (
                <div className="px-5 py-3 border-t border-gray-200 bg-amber-50">
                  <p className="text-[11px] text-amber-800">
                    Nothing is imported while a row has a problem — a half-imported region tree
                    is worse than none. Fix the rows above in the file and upload it again.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Import finished</h3>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
                  <CheckCircle2 size={15} className="text-emerald-600" />
                  {[['Province', results.added.provinces], ['District', results.added.districts],
                    ['Electorate', results.added.electorates], ['Police Station', results.added.stations]]
                    .map(([label, n]) => (
                      <span key={label} className="text-gray-700">
                        <span className="font-semibold text-gray-900 tabular-nums">{n}</span> {label}{n !== 1 ? 's' : ''}
                      </span>
                    ))}
                </div>

                {results.failed.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-[13px] mb-1.5">
                      <AlertCircle size={15} className="text-red-600" />
                      <span className="font-semibold text-gray-800">{results.failed.length} could not be added</span>
                    </div>
                    <ul className="space-y-1">
                      {results.failed.map((f, i) => (
                        <li key={i} className="text-[11px] text-red-700">
                          {f.sheet} row {f._row} ({f.name || 'no name'}): {f.message}
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] text-gray-500 mt-2">
                      Re-uploading the same file is safe — the rows that went in are now marked as existing and skipped.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <button onClick={refreshLists} disabled={loadingLists || importing} className={BTN_GHOST}>
            <RefreshCw size={13} className={loadingLists ? 'animate-spin' : ''} /> Reload lists
          </button>

          <div className="flex items-center gap-2">
            {importing && (
              <span className="text-[11px] text-gray-500 tabular-nums mr-1">
                {progress.label}: {progress.done} of {progress.total}…
              </span>
            )}
            <button onClick={onClose} disabled={importing} className={BTN_GHOST}>
              {results ? 'Close' : 'Cancel'}
            </button>
            {!results && (
              <button onClick={runImport}
                disabled={importing || loadingLists || !checked || blocked || checked.totalNew === 0}
                className={BTN_PRIMARY}>
                {importing
                  ? <><Loader2 size={13} className="animate-spin" /> Importing…</>
                  : <>Import {checked && checked.totalNew > 0 ? checked.totalNew : ''} Record{checked && checked.totalNew !== 1 ? 's' : ''} <ChevronRight size={13} /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}